<script setup>
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Icon from 'next/icon/Icon.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import EmptyMessage from 'components/widgets/EmptyMessage.vue';
import OrderCard from './OrderCard.vue';

import { defineProps, onMounted, ref, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  currentChat: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const orders = useMapGetter('tiktokShop/getOrders');
const uiFlags = useMapGetter('tiktokShop/getUIFlags');
const { t } = useI18n();

const activeTabIndex = ref(0);
const activeTabValue = ref('ALL');
const startOfTabs = ref(true);
const tabsContainer = ref(null);

const availableTabs = reactive([
  {
    value: 'ALL',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.ALL'),
    count: 0,
  },
  {
    value: 'UNPAID',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.UNPAID'),
    count: 0,
  },
  {
    value: 'ON_HOLD',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.ON_HOLD'),
    count: 0,
  },
  {
    value: 'AWAITING_SHIPMENT',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.AWAITING_SHIPMENT'),
    count: 0,
  },
  {
    value: 'PARTIALLY_SHIPPING',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.PARTIALLY_SHIPPING'),
    count: 0,
  },
  {
    value: 'AWAITING_COLLECTION',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.AWAITING_COLLECTION'),
    count: 0,
  },
  {
    value: 'IN_TRANSIT',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.IN_TRANSIT'),
    count: 0,
  },
  {
    value: 'DELIVERED',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.DELIVERED'),
    count: 0,
  },
  {
    value: 'COMPLETED',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.COMPLETED'),
    count: 0,
  },
  {
    value: 'CANCELLED',
    label: t('CONVERSATION.TIKTOKSHOP.ORDER.STATUS.CANCELLED'),
    count: 0,
  },
]);

const fetchOrders = () => {
  if (!props.currentChat?.id) return;
  store.dispatch('tiktokShop/getOrders', {
    conversationID: props.currentChat.id,
    orderStatus: activeTabValue.value,
  });
};

const handleTabChange = selectedTab => {
  activeTabValue.value = selectedTab.value;
  activeTabIndex.value = availableTabs.findIndex(
    tab => tab.value === selectedTab.value
  );
  fetchOrders();
};

const orderTabsScroll = left => {
  tabsContainer.value?.querySelector('.tabs')?.scrollTo({
    behavior: 'smooth',
    left: left,
  });
};

const handleGoStartTabs = () => {
  startOfTabs.value = true;
  orderTabsScroll(0);
};

const handleGoEndTabs = () => {
  startOfTabs.value = false;
  orderTabsScroll(1000);
};

watch(
  () => props.currentChat,
  () => {
    fetchOrders();
  },
  { immediate: true }
);

onMounted(() => {
  fetchOrders();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-slate-50 dark:bg-slate-800">
    <div id="tts-order-tabs" ref="tabsContainer">
      <a
        v-if="!startOfTabs"
        class="h-6 w-6 p-1 bg-white dark:bg-slate-900 text-center cursor-pointer border-r border-slate-100 dark:border-slate-700"
        @click="handleGoStartTabs"
      >
        <Icon icon="i-ri-contract-left-line" />
      </a>
      <TabBar
        class="bg-white"
        :tabs="availableTabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="handleTabChange"
      />
      <a
        v-if="startOfTabs"
        class="h-6 w-6 p-1 bg-white dark:bg-slate-900 text-center cursor-pointer border-l border-slate-100 dark:border-slate-700"
        @click="handleGoEndTabs"
      >
        <Icon icon="i-ri-contract-right-line" />
      </a>
    </div>
    <LoadingState v-if="uiFlags.isFetchingOrders" />
    <EmptyMessage
      v-else-if="orders.length === 0"
      :message="t('CONVERSATION.TIKTOKSHOP.ORDER.EMPTY_MESSAGE')"
    />
    <ul v-else class="flex flex-col p-2 overflow-y-auto">
      <li
        v-for="order in orders"
        :key="order.orderId"
        class="w-full flex flex-col p-2 mb-2 bg-white dark:bg-slate-900 rounded-md"
      >
        <OrderCard :order="order" :current-chat="currentChat" />
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
#tts-order-tabs {
  @apply flex flex-none border-b border-n-weak bg-white dark:bg-slate-900;

  .tabs {
    @apply flex-auto h-6 px-0;
    @apply overflow-x-auto bg-n-alpha-1 rounded-none;
    scrollbar-width: none;
    -ms-overflow-style: none;

    button {
      @apply border-none border-0 outline-none rounded-none;
      @apply text-xs bg-white dark:bg-slate-900;
      @apply h-6 px-3 py-1;
      @apply overflow-visible;

      &:hover,
      &.active {
        @apply border-b border-solid;
        @apply dark:text-slate-100 dark:border-slate-100;
        @apply bg-white dark:bg-slate-800 text-woot-700 border-woot-700;
      }
    }
  }

  a {
    @apply flex-none;
  }

  .divider {
    @apply hidden;
  }
}
</style>
